import { IconButton } from "@mui/material";
import { useA11yMessage } from "containers/A11yStatusProvider/A11yStatusContext";
import { REQUEST_FORM_FIELDS } from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { formatAddress } from "core/model/utils/location/formatters";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { IconType, TransitionalCareRequest } from "core/types";
import { format, fromUnixTime } from "date-fns";
import { MEDIUM_GREY, WHITE } from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { margin, padding, sizing } from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_16,
  FONT_WEIGHT_SEMI_BOLD,
} from "ds_legacy/materials/typography";
import { PencilIcon, Trash2Icon } from "lucide-react";
import { useTranslations } from "translations";
import { getSectionIdLabel } from ".";
import { useRequestListContext } from "../RequestListContext";
import { ACTION_TYPES } from "../RequestListContext/reducer";

export const CardIconButton = ({
  ariaLabel,
  Icon,
  onClick,
  testId,
}: {
  Icon: IconType;
  ariaLabel?: string;
  onClick: () => void;
  testId?: string;
}) => {
  return (
    <IconButton
      aria-label={ariaLabel}
      data-testid={testId}
      href=""
      size="small"
      sx={{ height: "fit-content" }}
      onClick={onClick}
    >
      <Icon size={FONT_SIZE_16} style={{ fontSize: FONT_SIZE_16 }} />
    </IconButton>
  );
};

export const TransitionalCareRequestCard = ({
  id,
  request,
}: {
  id: number;
  request: TransitionalCareRequest;
}) => {
  const { currentLocale } = useDateLocale();
  const translations = useTranslations();
  const { dispatch } = useRequestListContext();
  const getOntology = useGetOntology();
  const { updateA11yMessage } = useA11yMessage();

  return (
    <HorizontalLayout
      backgroundColor={WHITE}
      boxSizing="border-box"
      data-testid={`request-card-${id}`}
      minHeight={sizing(12)}
      overflow="visible"
      padding={padding(1, 2)}
      width="100%"
    >
      <VerticalLayout boxSizing="border-box" width="3%">
        <Body
          data-testid={`sectionid-${id}`}
          fontWeight={FONT_WEIGHT_SEMI_BOLD}
          margin="0"
        >
          {getSectionIdLabel(id)}
        </Body>
      </VerticalLayout>
      <VerticalLayout justify="space-between" width="75%">
        <Body
          data-testid="careprovider-name"
          fontWeight={FONT_WEIGHT_SEMI_BOLD}
          margin="0"
        >
          {request[REQUEST_FORM_FIELDS.CAREPROVIDER_NAME]}
        </Body>
        <Body
          data-testid="careprovider-address"
          fontWeight={FONT_WEIGHT_SEMI_BOLD}
          margin="0"
        >
          {formatAddress({
            address: request[REQUEST_FORM_FIELDS.CAREPROVIDER_ADDRESS_ADDRESS],
            city: request[REQUEST_FORM_FIELDS.CAREPROVIDER_ADDRESS_CITY],
            zip_code: request[REQUEST_FORM_FIELDS.CAREPROVIDER_ADDRESS_ZIPCODE],
          })}
        </Body>
        <HorizontalLayout>
          <Body
            data-testid="sent-date"
            fontWeight={FONT_WEIGHT_SEMI_BOLD}
            margin="0"
          >
            {format(
              fromUnixTime(request[REQUEST_FORM_FIELDS.REQUEST_SENT_DATE] ?? 0),
              "P",
              {
                locale: currentLocale,
              },
            )}
          </Body>
          <Body
            data-testid="sent-time"
            fontWeight={FONT_WEIGHT_SEMI_BOLD}
            margin={margin(0, 0, 0, 1)}
          >
            {format(
              fromUnixTime(request[REQUEST_FORM_FIELDS.REQUEST_SENT_TIME] ?? 0),
              "HH:mm",
              {
                locale: currentLocale,
              },
            )}
          </Body>
          <Body fontWeight={FONT_WEIGHT_SEMI_BOLD} margin={margin(0, 0, 0, 1)}>
            -
          </Body>
          <Body
            data-testid="request-status"
            fontWeight={FONT_WEIGHT_SEMI_BOLD}
            margin={margin(0, 0, 0, 1)}
          >
            {getOntology({
              key: request[REQUEST_FORM_FIELDS.REQUEST_STATUS],
              type: ONTOLOGIES.TRANSITIONAL_CARE_REQUEST_STATUS,
            })}
          </Body>
        </HorizontalLayout>
      </VerticalLayout>
      <HorizontalLayout justify="space-between" overflow="visible" width="15%">
        {request.manual && (
          <Body color={MEDIUM_GREY} data-testid="manually-added" margin="0">
            {
              translations.patientForms.transitionalCareForm.sectionE
                .manuallyAddedProvider
            }
          </Body>
        )}
      </HorizontalLayout>
      <HorizontalLayout justify="space-between" overflow="visible" width="7%">
        <CardIconButton
          ariaLabel={
            translations.patientForms.transitionalCareForm.sectionE
              .editButtonAriaLabel
          }
          Icon={PencilIcon}
          testId="edit-button"
          onClick={() =>
            dispatch({
              payload: { request },
              type: ACTION_TYPES.OPEN_EDIT,
            })
          }
        />
        <CardIconButton
          ariaLabel={
            translations.patientForms.transitionalCareForm.sectionE
              .deleteButtonAria
          }
          Icon={Trash2Icon}
          testId="delete-button"
          onClick={() => {
            dispatch({
              payload: { id: request.id },
              type: ACTION_TYPES.DELETE,
            });
            updateA11yMessage(
              translations.patientForms.transitionalCareForm.sectionE.deletionScreenReaderAnnouncement(
                {
                  providerName: request[REQUEST_FORM_FIELDS.CAREPROVIDER_NAME],
                },
              ),
            );
          }}
        />
      </HorizontalLayout>
    </HorizontalLayout>
  );
};
